import React, { useState } from 'react';
import Select from '../../../components/ui/Select';
import Input from '../../../components/ui/Input';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';

const AuditLogSection = () => {
  const [filters, setFilters] = useState({
    dateRange: '7',
    actionType: 'all',
    searchTerm: ''
  });

  const [auditLogs] = useState([
    {
      id: 1,
      timestamp: "2025-01-19 11:15:32",
      action: "Profile Updated",
      category: "account",
      details: "Updated phone number and department information",
      ipAddress: "192.168.1.100",
      userAgent: "Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36",
      location: "Washington, DC",
      severity: "low"
    },
    {
      id: 2,
      timestamp: "2025-01-19 09:45:18",
      action: "Password Changed",
      category: "security",
      details: "Password successfully updated with strong encryption",
      ipAddress: "192.168.1.100",
      userAgent: "Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36",
      location: "Washington, DC",
      severity: "medium"
    },
    {
      id: 3,
      timestamp: "2025-01-19 08:30:45",
      action: "Login Successful",
      category: "authentication",
      details: "Successful login with two-factor authentication",
      ipAddress: "192.168.1.100",
      userAgent: "Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36",
      location: "Washington, DC",
      severity: "low"
    },
    {
      id: 4,
      timestamp: "2025-01-18 16:22:10",
      action: "API Key Generated",
      category: "api",
      details: "Created new API key 'Production Integration' with read/write permissions",
      ipAddress: "10.0.0.50",
      userAgent: "Mozilla/5.0 (Macintosh; Intel Mac OS X 10_15_7) AppleWebKit/537.36",
      location: "Arlington, VA",
      severity: "medium"
    },
    {
      id: 5,
      timestamp: "2025-01-18 14:15:33",
      action: "Notification Settings Updated",
      category: "preferences",
      details: "Modified email notification preferences for incident assignments",
      ipAddress: "10.0.0.50",
      userAgent: "Mozilla/5.0 (Macintosh; Intel Mac OS X 10_15_7) AppleWebKit/537.36",
      location: "Arlington, VA",
      severity: "low"
    },
    {
      id: 6,
      timestamp: "2025-01-18 11:45:22",
      action: "Failed Login Attempt",
      category: "security",
      details: "Failed login attempt - incorrect password",
      ipAddress: "203.0.113.45",
      userAgent: "Mozilla/5.0 (X11; Linux x86_64) AppleWebKit/537.36",
      location: "Unknown Location",
      severity: "high"
    },
    {
      id: 7,
      timestamp: "2025-01-17 15:30:18",
      action: "Two-Factor Authentication Enabled",
      category: "security",
      details: "Successfully enabled 2FA with authenticator app",
      ipAddress: "192.168.1.101",
      userAgent: "Mozilla/5.0 (iPhone; CPU iPhone OS 17_0 like Mac OS X)",
      location: "Washington, DC",
      severity: "medium"
    },
    {
      id: 8,
      timestamp: "2025-01-17 10:20:45",
      action: "Profile Viewed",
      category: "account",
      details: "Accessed account settings page",
      ipAddress: "192.168.1.100",
      userAgent: "Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36",
      location: "Washington, DC",
      severity: "low"
    }
  ]);

  const dateRangeOptions = [
    { value: '1', label: 'Last 24 hours' },
    { value: '7', label: 'Last 7 days' },
    { value: '30', label: 'Last 30 days' },
    { value: '90', label: 'Last 90 days' },
    { value: 'all', label: 'All time' }
  ];

  const actionTypeOptions = [
    { value: 'all', label: 'All Actions' },
    { value: 'authentication', label: 'Authentication' },
    { value: 'security', label: 'Security' },
    { value: 'account', label: 'Account Changes' },
    { value: 'api', label: 'API Activity' },
    { value: 'preferences', label: 'Preferences' }
  ];

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const getActionIcon = (category) => {
    const iconMap = {
      authentication: 'LogIn',
      security: 'Shield',
      account: 'User',
      api: 'Key',
      preferences: 'Settings'
    };
    return iconMap?.[category] || 'Activity';
  };

  const getSeverityColor = (severity) => {
    const colorMap = {
      low: 'text-success',
      medium: 'text-warning',
      high: 'text-error'
    };
    return colorMap?.[severity] || 'text-muted-foreground';
  };

  const getSeverityBg = (severity) => {
    const bgMap = {
      low: 'bg-success',
      medium: 'bg-warning',
      high: 'bg-error'
    };
    return bgMap?.[severity] || 'bg-muted';
  };

  const filteredLogs = auditLogs?.filter(log => {
    const matchesCategory = filters?.actionType === 'all' || log?.category === filters?.actionType;
    const matchesSearch = !filters?.searchTerm || 
      log?.action?.toLowerCase()?.includes(filters?.searchTerm?.toLowerCase()) ||
      log?.details?.toLowerCase()?.includes(filters?.searchTerm?.toLowerCase());
    
    return matchesCategory && matchesSearch;
  });

  const handleExportLogs = () => {
    console.log('Exporting audit logs...');
  };

  const formatUserAgent = (userAgent) => {
    if (userAgent?.includes('Windows')) return 'Windows Desktop';
    if (userAgent?.includes('Macintosh')) return 'macOS Desktop';
    if (userAgent?.includes('iPhone')) return 'iPhone';
    if (userAgent?.includes('Android')) return 'Android Device';
    if (userAgent?.includes('Linux')) return 'Linux Desktop';
    return 'Unknown Device';
  };

  return (
    <div className="bg-card rounded-lg border border-border p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-3">
          <div className="w-12 h-12 bg-warning rounded-full flex items-center justify-center">
            <Icon name="FileText" size={24} color="white" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-foreground">Audit Log</h3>
            <p className="text-sm text-muted-foreground">View your account activity history</p>
          </div>
        </div>
        <Button
          variant="outline"
          iconName="Download"
          iconPosition="left"
          onClick={handleExportLogs}
        >
          Export Logs
        </Button>
      </div>
      {/* Filters */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <Select
          label="Date Range"
          options={dateRangeOptions}
          value={filters?.dateRange}
          onChange={(value) => handleFilterChange('dateRange', value)}
        />

        <Select
          label="Action Type"
          options={actionTypeOptions}
          value={filters?.actionType}
          onChange={(value) => handleFilterChange('actionType', value)}
        />

        <Input
          label="Search"
          type="search"
          placeholder="Search actions or details..."
          value={filters?.searchTerm}
          onChange={(e) => handleFilterChange('searchTerm', e?.target?.value)}
        />
      </div>
      {/* Audit Log Entries */}
      <div className="space-y-3">
        {filteredLogs?.length > 0 ? (
          filteredLogs?.map((log) => (
            <div key={log?.id} className="p-4 bg-background rounded-lg border border-border hover:shadow-sm transition-shadow duration-200">
              <div className="flex items-start justify-between">
                <div className="flex items-start space-x-3">
                  <div className="w-10 h-10 bg-muted rounded-lg flex items-center justify-center mt-1">
                    <Icon name={getActionIcon(log?.category)} size={18} />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center space-x-2 mb-1">
                      <h4 className="font-medium text-foreground">{log?.action}</h4>
                      <span className={`w-2 h-2 rounded-full ${getSeverityBg(log?.severity)}`} />
                    </div>
                    <p className="text-sm text-muted-foreground mb-2">{log?.details}</p>
                    <div className="flex items-center space-x-4 text-xs text-muted-foreground">
                      <div className="flex items-center space-x-1">
                        <Icon name="Clock" size={12} />
                        <span>{log?.timestamp}</span>
                      </div>
                      <div className="flex items-center space-x-1">
                        <Icon name="MapPin" size={12} />
                        <span>{log?.location}</span>
                      </div>
                      <div className="flex items-center space-x-1">
                        <Icon name="Globe" size={12} />
                        <span>{log?.ipAddress}</span>
                      </div>
                      <div className="flex items-center space-x-1">
                        <Icon name="Monitor" size={12} />
                        <span>{formatUserAgent(log?.userAgent)}</span>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  <span className={`px-2 py-1 text-xs font-medium rounded-full ${getSeverityColor(log?.severity)} bg-opacity-10`}>
                    {log?.severity?.charAt(0)?.toUpperCase() + log?.severity?.slice(1)}
                  </span>
                  <span className={`px-2 py-1 text-xs font-medium rounded-full bg-muted text-muted-foreground`}>
                    {log?.category}
                  </span>
                </div>
              </div>
            </div>
          ))
        ) : (
          <div className="text-center py-12">
            <Icon name="Search" size={48} className="text-muted-foreground mx-auto mb-4" />
            <h4 className="text-lg font-medium text-foreground mb-2">No Logs Found</h4>
            <p className="text-muted-foreground">No audit logs match your current filters.</p>
          </div>
        )}
      </div>
      {/* Load More */}
      {filteredLogs?.length > 0 && (
        <div className="flex justify-center mt-6">
          <Button
            variant="outline"
            iconName="ChevronDown"
            iconPosition="right"
          >
            Load More Entries
          </Button>
        </div>
      )}
      {/* Summary Stats */}
      <div className="mt-6 pt-6 border-t border-border">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
          <div>
            <p className="text-2xl font-semibold text-foreground">{auditLogs?.length}</p>
            <p className="text-sm text-muted-foreground">Total Events</p>
          </div>
          <div>
            <p className="text-2xl font-semibold text-success">
              {auditLogs?.filter(log => log?.severity === 'low')?.length}
            </p>
            <p className="text-sm text-muted-foreground">Low Priority</p>
          </div>
          <div>
            <p className="text-2xl font-semibold text-warning">
              {auditLogs?.filter(log => log?.severity === 'medium')?.length}
            </p>
            <p className="text-sm text-muted-foreground">Medium Priority</p>
          </div>
          <div>
            <p className="text-2xl font-semibold text-error">
              {auditLogs?.filter(log => log?.severity === 'high')?.length}
            </p>
            <p className="text-sm text-muted-foreground">High Priority</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AuditLogSection;